'use client';

import React, { useEffect, useSyncExternalStore } from 'react';

// Shared across instances. LoadingLottie mounts constantly (route Suspense
// fallbacks, isLoading states), and each fresh instance used to re-import
// react-lottie and re-fetch its JSON (served with max-age=0, so a round trip)
// before drawing anything; short loads finished before the loader appeared.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LottieComponentType = React.ComponentType<any>;

let lottieComponent: LottieComponentType | null = null;
let lottieComponentRequest: Promise<void> | null = null;
const animationDataByPath = new Map<string, unknown>();
const animationDataRequests = new Map<string, Promise<void>>();
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((listener) => listener());

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Starts loading react-lottie and the animation at `path`, once per path. */
export const preloadLottie = (path: string) => {
  if (!lottieComponent && !lottieComponentRequest) {
    lottieComponentRequest = import('react-lottie')
      .then((mod) => {
        lottieComponent = mod.default;
        notify();
      })
      .catch((error) => {
        // Cleared so the next mount retries.
        lottieComponentRequest = null;
        console.error('Error loading react-lottie:', error);
      });
  }

  if (!animationDataByPath.has(path) && !animationDataRequests.has(path)) {
    animationDataRequests.set(path, fetch(path)
      .then((response) => response.json())
      .then((data) => {
        animationDataByPath.set(path, data);
        notify();
      })
      .catch((error) => {
        console.error('Error fetching Lottie animation data:', error);
      })
      .finally(() => {
        animationDataRequests.delete(path);
      }));
  }
};

const getServerSnapshot = () => null;

interface LottieAnimationProps {
  path: string;
  width?: string;
  height?: string;
  loop?: boolean;
  autoplay?: boolean;
  className?: string;
  slice?: boolean;
  speed?: number;
  onReady?: () => void;
}

const LottieAnimation: React.FC<LottieAnimationProps> = ({
  path,
  width = '100%',
  height = '100%',
  loop = true,
  autoplay = true,
  className = '',
  slice = false,
  speed = 1,
  onReady,
}) => {
  // The server snapshot (null) keeps hydration matching the server-rendered
  // placeholder; any instance mounted after that reads the cache synchronously
  // and draws on its first render.
  const LottieComponent = useSyncExternalStore(subscribe, () => lottieComponent, getServerSnapshot);
  const animationData = useSyncExternalStore(
    subscribe,
    () => animationDataByPath.get(path) ?? null,
    getServerSnapshot,
  );

  useEffect(() => {
    preloadLottie(path);
  }, [path]);

  const isReady = Boolean(animationData && LottieComponent);

  useEffect(() => {
    if (isReady) {
      onReady?.();
    }
  }, [isReady, onReady]);

  if (!animationData || !LottieComponent) {
    // Reserve the eventual size of the animation so callers don't observe a
    // layout shift when the JSON + react-lottie chunks finish loading.
    return <div className={className} style={{ width, height }} aria-hidden="true" />;
  }

  const defaultOptions = {
    loop,
    autoplay,
    animationData,
    rendererSettings: {
      preserveAspectRatio: slice ? 'xMidYMid slice' : 'xMidYMid meet',
    },
  };

  return (
    <div
      className={`${className}`}
      style={{ width, height }}
    >
      <LottieComponent
        options={defaultOptions}
        width="100%"
        height="100%"
        isClickToPauseDisabled={true}
        speed={speed}
      />
    </div>
  );
};

export default LottieAnimation;
